import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Power, FileCode2, Settings2, FlaskConical, ListTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { useToast } from '@/components/shared/Toast';
import { CodeEditor } from '@/components/webhooks/CodeEditor';
import { SandboxTestPanel } from '@/components/webhooks/SandboxTestPanel';
import { DeliveryLogsTable } from '@/components/webhooks/DeliveryLogsTable';
import { DeliveryLogDrawer } from '@/components/webhooks/DeliveryLogDrawer';
import {
  useGetWebhookQuery,
  useSetWebhookEnabledMutation,
} from '@/store/api/communicationApi';
import { webhookIdentifier, type DeliveryLog, type Webhook } from '@/types';

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-sm">{value}</div>
    </div>
  );
}

export function WebhookDetailPage() {
  const { webhookId } = useParams<{ webhookId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [selectedLog, setSelectedLog] = useState<DeliveryLog | null>(null);

  const { data: webhook, isLoading, isError } = useGetWebhookQuery(webhookId ?? '', { skip: !webhookId });
  const [setEnabled, { isLoading: toggling }] = useSetWebhookEnabledMutation();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !webhook) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/webhooks')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <p className="pt-8 text-center text-destructive">Webhook not found or failed to load.</p>
      </div>
    );
  }

  const id = webhookId ?? webhookIdentifier(webhook);

  const handleToggle = async (w: Webhook) => {
    try {
      await setEnabled({ id: webhookIdentifier(w), enabled: w.status !== 'active' }).unwrap();
      toastSuccess(`Webhook ${w.status === 'active' ? 'disabled' : 'enabled'}`);
    } catch {
      toastError('Failed to update webhook status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/webhooks')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <span className="truncate">{webhook.name}</span>
            <Badge variant={webhook.status === 'active' ? 'success' : 'secondary'}>
              {webhook.status}
            </Badge>
          </h1>
          {webhook.description && (
            <p className="truncate text-sm text-muted-foreground">{webhook.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleToggle(webhook)}
            disabled={toggling}
            className="gap-1.5"
          >
            <Power className="h-4 w-4" />
            {webhook.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
          <Button onClick={() => navigate(`/webhooks/${id}/edit`)} className="gap-1.5">
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">
            <Settings2 className="mr-1.5 h-4 w-4" /> Config
          </TabsTrigger>
          <TabsTrigger value="test">
            <FlaskConical className="mr-1.5 h-4 w-4" /> Test Run
          </TabsTrigger>
          <TabsTrigger value="logs">
            <ListTree className="mr-1.5 h-4 w-4" /> Delivery Logs
          </TabsTrigger>
        </TabsList>

        {/* ── Config tab ─────────────────────────────────────────────── */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <MetadataRow label="Owner" value={<span className="font-mono text-xs">{webhook.ownerUserId}</span>} />
                <MetadataRow label="Version" value={webhook.version} />
                <MetadataRow label="Created" value={webhook.createdAt ? new Date(webhook.createdAt).toLocaleString() : '—'} />
                <MetadataRow label="Updated" value={webhook.updatedAt ? new Date(webhook.updatedAt).toLocaleString() : '—'} />
                <MetadataRow label="Trigger" value={<Badge variant="outline">{webhook.trigger?.kind}</Badge>} />
                <MetadataRow
                  label="Policies"
                  value={
                    (webhook.policyIds?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(webhook.policyIds ?? []).map((p) => (
                          <span key={p} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                            {p}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'None'
                    )
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileCode2 className="h-4 w-4 text-muted-foreground" /> Handler code
              </CardTitle>
              <CardDescription>Read-only view of the sandboxed handler.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-hidden rounded-md border">
                <CodeEditor language="javascript" value={webhook.code} readOnly height={360} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Trigger configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <JsonViewer data={webhook.trigger} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Test Run tab ───────────────────────────────────────────── */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sandbox test run</CardTitle>
              <CardDescription>
                Execute the handler with a sample input without producing real deliveries.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SandboxTestPanel webhookId={id} code={webhook.code} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Delivery Logs tab ─────────────────────────────────────── */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Delivery logs</CardTitle>
            </CardHeader>
            <CardContent>
              <DeliveryLogsTable webhookId={id} onSelectLog={setSelectedLog} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DeliveryLogDrawer
        log={selectedLog}
        open={!!selectedLog}
        onOpenChange={(open) => !open && setSelectedLog(null)}
      />
    </div>
  );
}